import { TypedDocumentNode, gql } from '@apollo/client';

import { OnboardingStepDialog } from '@sorare/core/src/components/rivals/OnboardingStepDialog';
import { isType } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { Price } from './Price';
import { Rarity } from './Rarity';
import { SerialNumber } from './SerialNumber';
import {
  CardOpeningOnboardingDialogQuery,
  CardOpeningOnboardingDialogQueryVariables,
  CardOpeningOnboardingDialog_probabilisticBundle,
} from './__generated__/index.graphql';

const BOX_OPENING_ONBOARDING_DIALOG = gql`
  query CardOpeningOnboardingDialogQuery($playerSlug: String!) {
    tokens {
      tokenPrices(
        playerSlug: $playerSlug
        rarity: limited
        first: 1
        seasonEligibility: IN_SEASON
      ) {
        id
        ...Price_tokenPrice
      }
    }
  }
  ${Price.fragments.tokenPrice}
` as TypedDocumentNode<
  CardOpeningOnboardingDialogQuery,
  CardOpeningOnboardingDialogQueryVariables
>;

type Props = {
  open: boolean;
  onClose: () => void;
  bundle: CardOpeningOnboardingDialog_probabilisticBundle;
};
export const CardOpeningOnboardingDialog = ({
  open,
  onClose,
  bundle,
}: Props) => {
  const firstItem = bundle.items[0];
  const cardItem = isType(firstItem, 'ProbabilisticBundleSlotCardItem')
    ? firstItem
    : null;
  const playerSlug = cardItem?.anyCard.anyPlayer.slug || '';

  const { data } = useQuery(BOX_OPENING_ONBOARDING_DIALOG, {
    variables: { playerSlug },
    skip: !playerSlug,
  });

  const anyPlayer = cardItem?.anyCard.anyPlayer;
  const serialNumber = cardItem?.anyCard.serialNumber;

  if (!isType(anyPlayer, 'Player')) {
    return null;
  }

  const tokenPrice = data?.tokens.tokenPrices[0];

  return (
    <OnboardingStepDialog open={open} onClose={onClose}>
      <Rarity card={cardItem?.anyCard} />
      {serialNumber && (
        <SerialNumber card={cardItem.anyCard} serialNumber={serialNumber} />
      )}
      {tokenPrice && <Price tokenPrice={tokenPrice} card={cardItem?.anyCard} />}
    </OnboardingStepDialog>
  );
};

CardOpeningOnboardingDialog.fragments = {
  probabilisticBundle: gql`
    fragment CardOpeningOnboardingDialog_probabilisticBundle on ProbabilisticBundle {
      id
      items {
        id
        ... on ProbabilisticBundleSlotCardItem {
          id
          anyCard {
            slug
            anyPlayer {
              slug
            }
            serialNumber
            ...Price_anyCard
            ...SerialNumber_anyCard
            ...Rarity_anyCard
          }
        }
      }
    }
    ${Rarity.fragments.anyCard}
    ${SerialNumber.fragments.anyCard}
    ${Price.fragments.anyCard}
  ` as TypedDocumentNode<CardOpeningOnboardingDialog_probabilisticBundle>,
};
